// @ts-strict-ignore
import { InputBlock, makeTupleFieldName } from '@coast/schemas';
import { useCallback, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { InputBlockMedia } from '@shared/components/blocks/lib/inputFile/InputBlockMedia';
import { useFileUpload } from '@shared/components/blocks/lib/inputFile/hooks';
import { MBQuestionInputActionButton } from '@shared/components/blocks/lib/inputQuestion/actionButton';
import { MBCardInputBlockQuestionCheckbox } from '@shared/components/blocks/lib/inputQuestion/checkbox';
import { MBQuestionFile } from '@shared/components/blocks/lib/inputQuestion/file';
import { MBQuestionNotes } from '@shared/components/blocks/lib/inputQuestion/notes';
import { MBCardInputBlockQuestionSingleSelect } from '@shared/components/blocks/lib/inputQuestion/singleSelect';
import { MBCardInputBlockQuestionTextArea } from '@shared/components/blocks/lib/inputQuestion/textArea';
import { FileUpload } from '@shared/components/blocks/types';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1 } from '@shared/components/typography';
import { useSelector } from '@shared/store';
import { grid } from '@shared/styles';
import { useIsDesktop, useTrigger } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { usePermission } from '@shared/util/permissions/hooks';

interface Props {
  block: InputBlock;
}

export function MBCardInputBlockQuestion({ block }: Props) {
  const mode = (block as any).mode || 'creation';
  const isDesktop = useIsDesktop();
  const anchorName = `--input-question-${block.name}`;
  const notesAnchorName = `--input-question-${block.name}-notes`;
  const { channelId } = useContext(ChannelPermissionsContext);
  const canUpdate = usePermission({ action: 'update', subject: 'Card', channelId }).enabled;
  const { setFieldValue, values, submitForm } = useContext(CardFormContext);
  const [notesTrigger, onNotesPress] = useTrigger();
  const [answerTrigger, onAnswerPress] = useTrigger();
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const maxImageWidth = isDesktop ? 300 : viewportWidth / 3;
  const maxImageHeight = (maxImageWidth * 2) / 3;
  const minImageWidth = maxImageWidth / 3;

  const fieldName = block.name;

  const persistForm = useCallback(() => {
    if (mode === 'completion') {
      submitForm();
    }
  }, [mode, submitForm]);

  const onChange = useCallback(
    (value: any) => {
      setFieldValue(fieldName, value);
      persistForm();
    },
    [fieldName, persistForm, setFieldValue],
  );

  const notesFieldName = makeTupleFieldName(fieldName, 'notes');
  const notesValue = values[notesFieldName];
  const onNotesChange = useCallback(
    (value: string) => {
      setFieldValue(notesFieldName, value);
      persistForm();
    },
    [setFieldValue, notesFieldName, persistForm],
  );

  const fileFieldName = makeTupleFieldName(fieldName, 'file');
  const fileValue: FileUpload[] = useMemo(
    () => values[fileFieldName] ?? [],
    [values, fileFieldName],
  );

  const onFileAdded = useCallback(
    file => {
      const newValues = [...fileValue];
      newValues.push(file);
      setFieldValue(fileFieldName, newValues);
      persistForm();
    },
    [fileValue, setFieldValue, fileFieldName, persistForm],
  );

  const onFileRemoved = useCallback(
    file => {
      const newValues = fileValue.filter(value => {
        return value.url !== file.url;
      });
      setFieldValue(fileFieldName, newValues);
      persistForm();
    },
    [fileValue, setFieldValue, fileFieldName, persistForm],
  );

  const { showFilePicker, uploading } = useFileUpload({
    onFileAdded: onFileAdded,
    onFileRemoved: onFileRemoved,
  });

  return (
    <MBView style={styles.container}>
      {block.fieldType === 'questionSingleSelect' && (
        <MBCardInputBlockQuestionSingleSelect
          fieldName={fieldName}
          label={block.label}
          trigger={answerTrigger}
          disabled={!canUpdate}
          max={block.validation?.max}
          onChange={onChange}
        />
      )}
      {block.fieldType === 'questionCheckbox' && (
        <MBCardInputBlockQuestionCheckbox
          fieldName={fieldName}
          label={block.label}
          disabled={!canUpdate}
          onChange={onChange}
        />
      )}
      {block.fieldType === 'questionTextArea' && (
        <MBCardInputBlockQuestionTextArea
          fieldName={fieldName}
          label={block.label}
          trigger={answerTrigger}
          disabled={!canUpdate}
          onChange={onChange}
          anchorName={anchorName}
        />
      )}
      {!!fileValue?.length && (
        <View paddingBottom={1}>
          <View style={{ borderRadius: grid(1), overflow: 'hidden' }}>
            <InputBlockMedia
              files={fileValue}
              maxHeight={maxImageHeight}
              maxWidth={maxImageWidth}
              minWidth={minImageWidth}
              compactFileView={false}
              editable={canUpdate}
              onRemoved={onFileRemoved}
            />
          </View>
        </View>
      )}
      {notesValue?.length > 0 && (
        <MBTouchableOpacity disabled={!canUpdate} onPress={onNotesPress}>
          <MBView style={styles.textarea} paddingVertical={2} paddingHorizontal={1}>
            <MBTextBody1>{notesValue}</MBTextBody1>
          </MBView>
        </MBTouchableOpacity>
      )}
      {canUpdate && (
        <View row>
          {block.fieldType !== 'questionCheckbox' && (
            <MBQuestionInputActionButton
              label={i18n('card.blocks.question.answerButtonLabel')}
              onPress={onAnswerPress}
              icon="pencil"
              anchorName={anchorName}
            />
          )}
          <View paddingRight={1} />
          <MBQuestionFile
            label={i18n('card.blocks.question.addFile')}
            onPress={showFilePicker}
            uploading={uploading}
          />
          <View paddingRight={1} />
          <MBQuestionNotes
            label={i18n('card.blocks.question.notes')}
            placeholder={i18n('card.blocks.question.notesPlaceholder')}
            value={notesValue}
            onChange={onNotesChange}
            trigger={notesTrigger}
            anchorName={notesAnchorName}
          />
        </View>
      )}
    </MBView>
  );
}

export const styles = StyleSheet.create({
  container: {
    marginVertical: grid(1),
    paddingTop: grid(2),
    paddingHorizontal: grid(1),
    marginHorizontal: grid(2),
    borderTopWidth: 1,
    borderTopColor: '#E6E6E6',
  },
  textarea: {
    backgroundColor: '#F7F7F7',
    borderRadius: grid(1),
    padding: grid(1),
    paddingLeft: grid(2),
    paddingRight: grid(2),
    marginBottom: grid(1),
  },
});
